import { Link } from "react-router-dom";
import {
  Eye, Euro, ShieldCheck, Clock, TrendingUp, Handshake, ArrowRight, Check,
} from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";

const BANNER = "https://images.unsplash.com/photo-1556630279-0ecfac70eaf2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBpdGFsaWFuJTIwdmlsbGElMjB0dXNjYW55JTIwcG9vbHxlbnwwfHx8fDE3ODkxMzYxNDZ8MA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  { icon: Eye, tag: "+ Visibilità", title: "Ottimizzazione annunci", desc: "Testi, foto e strategie per rendere la tua struttura irresistibile su ogni canale." },
  { icon: TrendingUp, tag: "+ Ricavi", title: "Prezzi e minimum stay dinamici", desc: "Tariffe che si adattano alla domanda per massimizzare i tuoi guadagni." },
  { icon: ShieldCheck, tag: "+ Sicurezza", title: "Self check-in e Alloggiati Web automatici", desc: "Automatizziamo il self check-in con invio dei dati degli ospiti al Portale Alloggiati Web della Polizia di Stato e conserviamo le ricevute di invio come previsto dalla normativa." },
  { icon: Clock, tag: "+ Tempo", title: "La tecnologia diventa semplice", desc: "Un'unica piattaforma per calendari, canali e messaggistica." },
  { icon: Euro, tag: "+ Dirette", title: "Aumenta le prenotazioni dirette", desc: "Il tuo mini-sito dedicato e il booking engine riducono le commissioni delle OTA." },
  { icon: Handshake, tag: "+ Certezza", title: "Gestione completa della tua casa", desc: "Ti presentiamo un partner locale e selezionato per la gestione totale e senza pensieri della tua struttura." },
];

const ADVANTAGES = [
  "Ricavi fino al +30%", "Niente stress con i prezzi", "Zero overbooking",
  "Più prenotazioni dirette", "App per prenotazioni e ospiti", "Assistenza e formazione",
];

export default function Proprietari() {
  return (
    <PublicLayout>
      <section className="relative">
        <div className="absolute inset-0">
          <img src={BANNER} alt="Villa italiana" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-violet/95 via-brand-violet/80 to-brand-plum/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-brand-tealGlow mb-4">Per i proprietari</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-white leading-[1.08] max-w-3xl">
            Sprigiona tutto il potenziale della tua casa vacanze o del tuo B&B.
          </h1>
          <p className="mt-5 text-lg sm:text-xl text-white/85 max-w-2xl">
            Quattro Accommodations è il partner ideale per i proprietari di case vacanze e piccoli B&B che vogliono aumentare i propri guadagni, semplificare la gestione e sfruttare al meglio il potenziale della propria struttura.
          </p>
          <Link to="/contatti" className="inline-block mt-8" data-testid="proprietari-cta-top">
            <Button className="rounded-full bg-brand-teal hover:bg-brand-tealDark text-brand-violet font-semibold px-8 h-12">Richiedi preventivo</Button>
          </Link>
        </div>
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-brand-teal mb-3">Sei aree di valore</p>
          <h2 className="text-3xl sm:text-4xl font-serif text-brand-plum">Cosa facciamo per te</h2>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s) => (
            <div key={s.title} className="rounded-2xl bg-white p-7 border border-brand-plum/10 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-brand-tealLight flex items-center justify-center mb-4">
                <s.icon className="text-brand-tealDark" size={22} />
              </div>
              <span className="text-xs font-mono uppercase tracking-wider text-brand-teal font-semibold">{s.tag}</span>
              <h3 className="font-serif text-xl text-brand-plum mt-1 mb-2">{s.title}</h3>
              <p className="text-sm text-brand-plum/60 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-3xl bg-brand-violet text-white p-8 md:p-10 flex flex-col md:flex-row items-center gap-6" data-testid="revenue-highlight">
          <div className="w-16 h-16 rounded-2xl bg-brand-teal/20 flex items-center justify-center shrink-0">
            <TrendingUp className="text-brand-tealGlow" size={30} />
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-brand-tealGlow mb-2">Il vantaggio più importante</p>
            <h3 className="font-serif text-2xl sm:text-3xl mb-2">Aumenta i ricavi fino al 30%</h3>
            <p className="text-white/80 leading-relaxed">Il nostro servizio si ripaga da solo: grazie all'ottimizzazione di annunci, prezzi e canali, aumentiamo le tue prenotazioni molto più del 4% della nostra commissione.</p>
          </div>
        </div>
      </section>

      {/* Relax band */}
      <section className="bg-brand-plum py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-5xl font-serif text-white">Rilassati, pensiamo noi a tutto.</h2>
          <p className="mt-5 text-lg text-white/75">Dì addio a configurazioni software, calendari, portali e inserzioni complicate.</p>
          <Link to="/contatti" className="inline-block mt-8">
            <Button className="rounded-full bg-brand-teal hover:bg-brand-tealDark text-brand-violet font-semibold px-8 h-12">Richiedi preventivo</Button>
          </Link>
        </div>
      </section>

      {/* Advantages + calculator */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-brand-teal mb-3">I vantaggi</p>
          <h2 className="text-3xl sm:text-4xl font-serif text-brand-plum mb-8">Tutto ciò che ottieni con Quattro</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {ADVANTAGES.map((a) => (
              <div key={a} className="flex items-center gap-3 rounded-xl bg-white border border-brand-plum/10 p-4">
                <span className="w-8 h-8 rounded-full bg-brand-tealLight flex items-center justify-center shrink-0">
                  <Check size={16} className="text-brand-tealDark" />
                </span>
                <span className="text-sm font-medium text-brand-plum">{a}</span>
              </div>
            ))}
          </div>
          <Link to="/prezzi" className="inline-flex items-center gap-1 mt-8 text-brand-teal font-medium hover:underline">
            Scopri il modello di prezzo <ArrowRight size={16} />
          </Link>
        </div>
        <div className="rounded-3xl bg-brand-plum text-white p-8 md:p-10" data-testid="owners-pay-on-results">
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-brand-tealGlow mb-3">Paghi solo quando ricevi prenotazioni</p>
          <h3 className="font-serif text-3xl mb-4">4% solo sulle prenotazioni ricevute</h3>
          <p className="text-white/75 leading-relaxed">Nessun canone fisso: i nostri interessi sono gli stessi dei tuoi. Se la struttura è chiusa o non riceve prenotazioni, non paghi commissioni. Channel Manager, PMS, Booking Engine e Revenue Management sono già inclusi.</p>
          <ul className="mt-6 space-y-2 text-sm text-white/85">
            {["Obiettivi condivisi", "Nessun costo quando non lavori", "Software professionale incluso", "Revenue Management senza costi aggiuntivi"].map((i) => (
              <li key={i} className="flex items-center gap-2"><Check size={15} className="text-brand-teal" /> {i}</li>
            ))}
          </ul>
          <p className="mt-5 text-xs text-white/55">+ €120 / anno per casa vacanza o B&B di costo amministrativo.</p>
        </div>
      </section>
    </PublicLayout>
  );
}
